import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  VerifyHublyFormFieldInput,
  VerifyHublyFormFieldResponse,
  VERIFY_HUBLY_FORM_FIELD,
} from '@plug/domains/organization/domains/affiliate/graphql/queries/verify-hubly-form-field';
import { useFormikContext, getIn } from 'formik';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import MaskedInput from '@plug/components/masked-input';
import { HublyFormInputProps } from './hubly-form-input.types';
import {
  VerifyHublyFormFieldTypeInput,
  VerifyHublyFormFieldTypeResponse,
  VERIFY_HUBLY_FORM_FIELD_TYPE,
} from '@plug/domains/organization/domains/affiliate/graphql/queries/verify-hubly-form-field-type';
import { getHublyFormFieldError, getHublyFormUnmaskedValue } from '@plug/helpers/hubly-form';
import useIsOrganizationAdmin from '@plug/hooks/use-is-organization-admin';
import { MARY_KAY_WHITELABEL } from '@plug/config/consts';
import { useCityCode } from '@plug/hooks/use-city-code';

const HublyFormInput = (props: HublyFormInputProps) => {
  const isOrganizationAdmin = useIsOrganizationAdmin();
  const name = props.requirement.value;
  const {
    initialValues,
    values,
    touched,
    errors,
    setStatus,
    status,
    ...formikProps
  } = useFormikContext();
  const [debouncedValue] = useDebounce(getHublyFormUnmaskedValue(getIn(values, props.name)), 800);
  const debouncedSearchFieldValueInExternalSource = useDebouncedCallback(() => {
    if (requirementWithAdditionalFields.additionalFields.type === 'cep') {
      searchFieldValueInExternalSource();
    }
  }, 800);
  const [serverErrors, setServerErrors] = useState({ [name]: '' });
  const [fieldValue, setFieldValue] = useState('');
  const { load } = useCityCode();
  const isMaryKay = MARY_KAY_WHITELABEL.includes(window.location.hostname);

  const requirementWithAdditionalFields = useMemo(() => {
    return {
      ...props.requirement,
      additionalFields:
        typeof props.requirement?.additionalFields === 'string'
          ? JSON.parse(props.requirement.additionalFields ?? '{}')
          : props.requirement?.additionalFields,
    };
  }, [props.requirement]);

  const { loading } = useQuery<VerifyHublyFormFieldResponse, VerifyHublyFormFieldInput>(
    VERIFY_HUBLY_FORM_FIELD,
    {
      variables: {
        input: {
          field: name,
          search: debouncedValue,
        },
      },
      onCompleted: data => {
        if (data && data?.verifyHublyFormField?.hasField) {
          return setServerErrors({
            [name]: 'Já existe um campo com esse valor. Verifique novamente, por favor.',
          });
        }

        setServerErrors({
          [name]: undefined,
        });

        setStatus('validated');
      },
      skip:
        !getIn(touched, props.name) ||
        !debouncedValue ||
        getIn(values, props.name) === getIn(initialValues, props.name) ||
        !requirementWithAdditionalFields.unique ||
        props.disabled,
    },
  );

  const { loading: loadingFieldTypeVerification } = useQuery<
    VerifyHublyFormFieldTypeResponse,
    VerifyHublyFormFieldTypeInput
  >(VERIFY_HUBLY_FORM_FIELD_TYPE, {
    variables: {
      input: {
        type: requirementWithAdditionalFields?.additionalFields?.type,
        value: debouncedValue as string,
      },
    },
    onCompleted: data => {
      if (data && !data?.verifyHublyFormFieldType) {
        return setServerErrors({
          [name]: getHublyFormFieldError(requirementWithAdditionalFields?.additionalFields?.type),
        });
      }

      setServerErrors({
        [name]: undefined,
      });

      setStatus('validated');
    },
    skip:
      loading ||
      !getIn(touched, props.name) ||
      !debouncedValue ||
      getIn(values, props.name) === getIn(initialValues, props.name) ||
      !requirementWithAdditionalFields?.additionalFields?.mask ||
      props.disabled,
  });

  const handleChange = e => {
    const rawValue = e.target.value;
    const numericValue = rawValue.replace(/\D/g, '');
    setFieldValue(numericValue);

    if (!isVerificationDisabled) setStatus('validating');
    props.onChange(e);
    debouncedSearchFieldValueInExternalSource();
  };

  const getLengthMask = (type: string, length: number) => {
    const maskLengths = {
      cep: length - 1,
      date: length - 2,
    };

    return maskLengths[type] || undefined;
  };

  const calculateMaxLength = () => {
    const { mask, type: fieldType } = requirementWithAdditionalFields?.additionalFields || {};

    if (!mask) return undefined;

    if (fieldValue === mask || /^\d+$/.test(fieldValue)) {
      return fieldValue.length < getLengthMask(fieldType, mask.length)
        ? mask.length * 2
        : mask.length;
    }

    return undefined;
  };

  const getError = (name, { touched, errors }) => {
    if (loading || loadingFieldTypeVerification) return '';

    const fieldTouched = getIn(touched, name);
    const backendError = getIn(serverErrors, requirementWithAdditionalFields.value);
    if (requirementWithAdditionalFields.value === 'ConsultantId')
      console.log({ backendError, serverErrors });
    const clientError = getIn(errors, name);

    if (clientError && fieldTouched) {
      return clientError;
    }
    if (backendError && fieldTouched) {
      return backendError;
    }
    return undefined;
  };

  const searchFieldValueInExternalSource = async () => {
    const cityCodeName = (props.requirements ?? []).find(
      item => item?.additionalFields?.type === 'city_code',
    )?.value;

    if (!cityCodeName) return;

    const value = getIn(values, props.name);
    const cityCodeFieldName = props.name.includes('customFields')
      ? `customFields.${cityCodeName}`
      : cityCodeName;

    if (getHublyFormUnmaskedValue(value)) {
      const code = await load(value);

      formikProps.setFieldValue(cityCodeFieldName, code);
    } else formikProps.setFieldValue(cityCodeFieldName, '');
  };

  const handleBlur = async (event: React.FocusEvent<HTMLInputElement, Element>) => {
    if (props.onBlur) await props.onBlur(event);
    if (requirementWithAdditionalFields.additionalFields.type === 'cep') {
      await searchFieldValueInExternalSource();
    }
  };

  const inputErrors = getError(props.name, { touched, errors });

  const isDisabled = useMemo(() => {
    if (requirementWithAdditionalFields?.additionalFields?.type === 'city_code') {
      return !!getIn(values, props.name);
    }

    return (
      props.disabled ||
      loading ||
      loadingFieldTypeVerification ||
      (requirementWithAdditionalFields?.additionalFields?.readOnly &&
        !isOrganizationAdmin &&
        MARY_KAY_WHITELABEL.includes(window.location.hostname))
    );
  }, [
    isOrganizationAdmin,
    loading,
    loadingFieldTypeVerification,
    props.disabled,
    props.name,
    requirementWithAdditionalFields,
    values,
  ]);

  const isValidating = loading || loadingFieldTypeVerification;

  const isVerificationDisabled =
    !requirementWithAdditionalFields?.additionalFields?.mask &&
    !requirementWithAdditionalFields?.unique;

  useEffect(() => {
    if ((loading || loadingFieldTypeVerification) && status !== 'validating') {
      setStatus('validating');
    }
  }, [loading, loadingFieldTypeVerification, setStatus, status]);

  useEffect(() => {
    if (requirementWithAdditionalFields.additionalFields.type === 'cep') {
      searchFieldValueInExternalSource();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirementWithAdditionalFields.additionalFields.type]);

  return (
    <MaskedInput
      {...props}
      onChange={handleChange}
      onBlur={e => {
        if (!isVerificationDisabled) setStatus('validating');

        return handleBlur(e);
      }}
      error={getIn(touched, props.name) && !!inputErrors && !isValidating}
      helperText={
        isValidating
          ? 'Verificando...'
          : getIn(touched, props.name) && !!inputErrors
          ? inputErrors.toString()
          : props.helperText
      }
      mask={
        isMaryKay && requirementWithAdditionalFields?.additionalFields?.mask === '99/99/9999'
          ? '99-99-9999'
          : requirementWithAdditionalFields?.additionalFields?.mask
      }
      disabled={isDisabled}
      maxLength={calculateMaxLength()}
    />
  );
};

export default HublyFormInput;
